import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import {
  Container, Row, Col, Card, CardBody, Spinner, Badge, Table, Input, InputGroup,
  Modal, ModalHeader, ModalBody, ModalFooter, Button
} from "reactstrap";
import { MapPin, Truck, CheckCircle, AlertCircle, XCircle, Users, Search, RefreshCw, DollarSign, Calendar, Shield, CreditCard, Activity, ArrowRight } from 'react-feather';
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import ReactApexChart from "react-apexcharts";
import * as signalR from "@microsoft/signalr";
import axios from "axios";
import L from "leaflet";

// Import Leaflet styles
import "leaflet/dist/leaflet.css";

// Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

// i18n
import { withTranslation } from "react-i18next";
import { API_WEB_URLS } from "../../constants/constAPI";

// Radar pulse styling for online available drivers
const mapPulseStyle = `
  @keyframes pinPulse {
    0% { transform: scale(0.5); opacity: 0.8; }
    100% { transform: scale(1.6); opacity: 0; }
  }
`;

// Helper component to center map on coordinates change
const ChangeMapCenter = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
};

// Create custom driver icons dynamically using Leaflet DivIcon
const getDriverIcon = (isOnline, isBusy) => {
  let pinColor = "#95a5a6"; // Offline (Grey)
  if (isOnline) {
    pinColor = isBusy ? "#f39c12" : "#2ecc71"; // In-Trip (Orange) : Available (Green)
  }

  return L.divIcon({
    html: `
      <div style="position: relative; width: 22px; height: 22px;">
        ${isOnline && !isBusy ? `
          <div style="
            position: absolute;
            width: 34px;
            height: 34px;
            left: -6px;
            top: -6px;
            border-radius: 50%;
            background-color: ${pinColor};
            opacity: 0.35;
            animation: pinPulse 1.8s infinite ease-in-out;
          "></div>
        ` : ''}
        <div style="
          position: absolute;
          background-color: ${pinColor};
          width: 22px;
          height: 22px;
          border-radius: 50%;
          border: 2.5px solid white;
          box-shadow: 0 0 6px rgba(0,0,0,0.35);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <div style="width: 7px; height: 7px; border-radius: 50%; background-color: white;"></div>
        </div>
      </div>
    `,
    className: "custom-leaflet-driver-icon",
    iconSize: [34, 34],
    iconAnchor: [17, 17]
  });
};

const Dashboard = props => {
  document.title = "Operations Control Dashboard | Kirayago";

  const [stats, setStats] = useState({
    summary: {
      TotalBookings: 0,
      PendingBookings: 0,
      AssignedBookings: 0,
      OngoingBookings: 0,
      CompletedBookings: 0,
      CancelledBookings: 0,
      TotalDrivers: 0,
      OnlineDrivers: 0,
      FreeDrivers: 0
    },
    bookingStatusDistribution: [],
    drivers: [],
    bookings: []
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Navigation / Filter State
  const [activeFilter, setActiveFilter] = useState("ALL"); 
  const [searchQuery, setSearchQuery] = useState("");
  const [mapCenter, setMapCenter] = useState([26.2389, 73.0243]); // Center on Jodhpur region
  const [selectedDriverId, setSelectedDriverId] = useState(null);

  // Drill-down Modals States
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverModalOpen, setDriverModalOpen] = useState(false);

  const [listModalType, setListModalType] = useState(null); // 'bookings' or 'drivers'
  const [listModalFilter, setListModalFilter] = useState(""); // Filter description
  const [listModalOpen, setListModalOpen] = useState(false);

  const signalRConnection = useRef(null);

  const BASE_URL = API_WEB_URLS.BASE;
  const STATS_API = `${BASE_URL}Dashboard/Stats/0/token`;

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(STATS_API);
      if (response.data && response.data.success) {
        setStats(response.data.data);
        setError(null);
      } else {
        setError("Failed to parse statistics response.");
      }
    } catch (err) {
      console.error("Error loading dashboard data:", err);
      setError("Network error loading dashboard statistics.");
    } finally {
      setLoading(false);
    }
  };

  // Setup SignalR connection
  useEffect(() => {
    fetchDashboardData();

    // Dynamically derive the Hub URL from API base URL
    const hubUrl = BASE_URL.replace("/api/V1/", "/bookingHub").replace("/api/v1/", "/bookingHub");
    
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl)
      .withAutomaticReconnect()
      .build();

    connection.on("BookingUpdated", (bookingId) => {
      console.log(`Real-time update: Booking ${bookingId} updated. Reloading stats...`);
      fetchDashboardData();
    });

    connection.on("NewBookingAvailable", () => {
      console.log("Real-time update: New booking available. Reloading stats...");
      fetchDashboardData();
    });

    connection.on("DriverLocationUpdated", (data) => {
      console.log("Real-time update: Driver location moved:", data);
      
      setStats(prev => {
        const updatedDrivers = prev.drivers.map(d => {
          if (d.ActiveBookingId && d.ActiveBookingId.toString() === data.bookingId?.toString()) {
            return {
              ...d,
              Latitude: data.latitude.toString(),
              Longitude: data.longitude.toString(),
              LastUpdated: new Date().toISOString()
            };
          }
          return d;
        });

        const updatedBookings = prev.bookings.map(b => {
          if (b.Id && b.Id.toString() === data.bookingId?.toString()) {
            return {
              ...b,
              DriverLatitude: data.latitude.toString(),
              DriverLongitude: data.longitude.toString()
            };
          }
          return b;
        });

        // Update selected modals in real-time
        if (selectedBooking && selectedBooking.Id?.toString() === data.bookingId?.toString()) {
          setSelectedBooking(prevB => ({
            ...prevB,
            DriverLatitude: data.latitude.toString(),
            DriverLongitude: data.longitude.toString()
          }));
        }

        return {
          ...prev,
          drivers: updatedDrivers,
          bookings: updatedBookings
        };
      });
    });

    connection.start()
      .then(() => {
        console.log("Admin Dashboard connected to BookingHub SignalR.");
        connection.invoke("JoinBookingGroup", "AdminDashboard")
          .catch(e => console.error("Error joining admin socket group:", e));
      })
      .catch(err => console.error("SignalR connection error:", err));

    signalRConnection.current = connection;

    return () => {
      if (signalRConnection.current) {
        signalRConnection.current.stop();
      }
    };
  }, []);

  const handleMarkerClick = (lat, lng, driverId) => {
    if (lat && lng) {
      setMapCenter([parseFloat(lat), parseFloat(lng)]);
      setSelectedDriverId(driverId);
    }
  };

  const openBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setBookingModalOpen(true);
  };

  const openDriverDetails = async (driverId) => {
    try {
      // Find local driver brief
      const brief = stats.drivers.find(d => d.DriverId === driverId);
      if (!brief) return;

      // Query full driver details from backend using MemberMaster
      const response = await axios.get(`${BASE_URL}Masters/0/token/MemberMasterEdit/Id/${driverId}`);
      if (response.data && response.data.success && response.data.data.DataList?.length > 0) {
        const fullProfile = response.data.data.DataList[0];
        setSelectedDriver({ ...brief, ...fullProfile });
        setDriverModalOpen(true);
      } else {
        // Fallback to brief details if full profile lookup fails
        setSelectedDriver(brief);
        setDriverModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching full driver profile:", error);
      const brief = stats.drivers.find(d => d.DriverId === driverId);
      if (brief) {
        setSelectedDriver(brief);
        setDriverModalOpen(true);
      }
    }
  };

  // Open generic lists popup modal
  const openListModal = (type, filter, title) => {
    setListModalType(type);
    setListModalFilter(filter);
    setListModalOpen(true);
  };

  // Filter list modals data
  const getListModalData = () => {
    if (listModalType === "bookings") {
      let bookings = stats.bookings || [];
      if (listModalFilter === "PENDING") return bookings.filter(b => b.StatusId === 1);
      if (listModalFilter === "ONGOING") return bookings.filter(b => b.StatusId >= 2 && b.StatusId <= 16);
      if (listModalFilter === "COMPLETED") return bookings.filter(b => b.StatusId === 17 || b.StatusId === 18);
      if (listModalFilter === "CANCELLED") return bookings.filter(b => b.StatusId === 19);
      return bookings;
    } else if (listModalType === "drivers") {
      let drivers = stats.drivers || [];
      if (listModalFilter === "AVAILABLE") return drivers.filter(d => d.IsOnline && !d.IsBusy);
      if (listModalFilter === "BUSY") return drivers.filter(d => d.IsOnline && d.IsBusy);
      if (listModalFilter === "OFFLINE") return drivers.filter(d => !d.IsOnline);
      if (listModalFilter === "ONLINE") return drivers.filter(d => d.IsOnline);
      return drivers;
    }
    return [];
  };

  // Filter logic for main bottom table
  const getFilteredBookings = () => {
    let list = stats.bookings || [];

    if (activeFilter === "PENDING") {
      list = list.filter(b => b.StatusId === 1);
    } else if (activeFilter === "ONGOING") {
      list = list.filter(b => b.StatusId >= 2 && b.StatusId <= 16);
    } else if (activeFilter === "COMPLETED") {
      list = list.filter(b => b.StatusId === 17 || b.StatusId === 18);
    } else if (activeFilter === "CANCELLED") {
      list = list.filter(b => b.StatusId === 19);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        b =>
          (b.BookingNo && b.BookingNo.toLowerCase().includes(q)) ||
          (b.CustomerName && b.CustomerName.toLowerCase().includes(q)) ||
          (b.DriverName && b.DriverName.toLowerCase().includes(q)) ||
          (b.PickupAddress && b.PickupAddress.toLowerCase().includes(q)) ||
          (b.Id && b.Id.toString().includes(q))
      );
    }

    return list;
  };

  // Chart configuration
  const barChartSeries = [
    {
      name: "Bookings",
      data: stats.bookingStatusDistribution.map(item => item.BookingCount)
    }
  ];

  const barChartOptions = {
    chart: {
      type: "bar",
      height: 350,
      toolbar: { show: false },
      events: {
        dataPointSelection: (event, chartContext, config) => {
          const statusItem = stats.bookingStatusDistribution[config.dataPointIndex];
          if (statusItem) {
            let filter = "ALL";
            const name = statusItem.StatusName.toUpperCase();
            if (name.includes("PENDING")) filter = "PENDING";
            else if (name.includes("CANCEL")) filter = "CANCELLED";
            else if (name.includes("ENDED") || name.includes("DELIVER")) filter = "COMPLETED";
            else filter = "ONGOING";

            openListModal("bookings", filter, `${statusItem.StatusName} Bookings`);
          }
        }
      }
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
        columnWidth: "45%",
        distributed: true
      }
    },
    colors: [
      "#3498db", "#f1c40f", "#e67e22", "#d35400", "#e74c3c",
      "#2c3e50", "#1abc9c", "#27ae60", "#2980b9", "#8e44ad",
      "#f39c12", "#c0392b", "#7f8c8d"
    ],
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: {
      categories: stats.bookingStatusDistribution.map(item => item.StatusName),
      labels: {
        style: { fontSize: "10px" }
      }
    },
    yaxis: { title: { text: "Count" } }
  };

  // Driver Status Donut Chart
  const activeCount = stats.drivers.filter(d => d.IsOnline && d.IsBusy).length;
  const freeCount = stats.drivers.filter(d => d.IsOnline && !d.IsBusy).length;
  const offlineCount = stats.drivers.filter(d => !d.IsOnline).length;

  const donutSeries = [freeCount, activeCount, offlineCount];
  const donutOptions = {
    labels: ["Available", "On Trip", "Offline"],
    colors: ["#2ecc71", "#f39c12", "#95a5a6"],
    chart: { 
      type: "donut",
      events: {
        dataPointSelection: (event, chartContext, config) => {
          const index = config.dataPointIndex;
          if (index === 0) openListModal("drivers", "AVAILABLE", "Available Drivers");
          if (index === 1) openListModal("drivers", "BUSY", "Drivers On Trip");
          if (index === 2) openListModal("drivers", "OFFLINE", "Offline Drivers");
        }
      }
    },
    legend: { position: "bottom" }
  };

  if (loading) {
    return (
      <div className="page-content text-center py-5">
        <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
        <h5 className="mt-3 text-muted">Analyzing live fleet registry and locations...</h5>
      </div>
    );
  }

  const filteredBookings = getFilteredBookings();
  const modalListData = getListModalData();

  return (
    <React.Fragment>
      <style dangerouslySetInnerHTML={{ __html: mapPulseStyle }} />
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={props.t("Control Tower")}
            breadcrumbItem={props.t("Live Fleet & Dispatch Center")}
          />

          {error && (
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <AlertCircle className="me-2" size={18} />
              <div>{error}</div>
            </div>
          )}

          {/* Metric Indicators Grid */}
          <Row>
            {/* Total Bookings */}
            <Col md="3" xl="2" className="col-6">
              <Card 
                className={`card-animate cursor-pointer border-bottom border-3 border-primary ${activeFilter === "ALL" ? "bg-soft-primary" : ""}`}
                onClick={() => openListModal("bookings", "ALL", "All Bookings")}
              >
                <CardBody className="p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted text-truncate mb-1">Total Bookings</p>
                      <h4 className="mb-0 fw-bold text-primary">{stats.summary.TotalBookings}</h4>
                    </div>
                    <div className="avatar-xs">
                      <span className="avatar-title rounded bg-soft-primary text-primary">
                        <Truck size={18} />
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Pending Bookings */}
            <Col md="3" xl="2" className="col-6">
              <Card 
                className={`card-animate cursor-pointer border-bottom border-3 border-info ${activeFilter === "PENDING" ? "bg-soft-info" : ""}`}
                onClick={() => openListModal("bookings", "PENDING", "Pending Bookings")}
              >
                <CardBody className="p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted text-truncate mb-1">Pending (Unassigned)</p>
                      <h4 className="mb-0 fw-bold text-info">{stats.summary.PendingBookings}</h4>
                    </div>
                    <div className="avatar-xs">
                      <span className="avatar-title rounded bg-soft-info text-info">
                        <MapPin size={18} />
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Ongoing Bookings */}
            <Col md="3" xl="2" className="col-6">
              <Card 
                className={`card-animate cursor-pointer border-bottom border-3 border-warning ${activeFilter === "ONGOING" ? "bg-soft-warning" : ""}`}
                onClick={() => openListModal("bookings", "ONGOING", "Ongoing Trips")}
              >
                <CardBody className="p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted text-truncate mb-1">Ongoing Rides</p>
                      <h4 className="mb-0 fw-bold text-warning">{stats.summary.OngoingBookings}</h4>
                    </div>
                    <div className="avatar-xs">
                      <span className="avatar-title rounded bg-soft-warning text-warning">
                        <RefreshCw size={18} />
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Completed Bookings */}
            <Col md="3" xl="2" className="col-6">
              <Card 
                className={`card-animate cursor-pointer border-bottom border-3 border-success ${activeFilter === "COMPLETED" ? "bg-soft-success" : ""}`}
                onClick={() => openListModal("bookings", "COMPLETED", "Completed Bookings")}
              >
                <CardBody className="p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted text-truncate mb-1">Completed</p>
                      <h4 className="mb-0 fw-bold text-success">{stats.summary.CompletedBookings}</h4>
                    </div>
                    <div className="avatar-xs">
                      <span className="avatar-title rounded bg-soft-success text-success">
                        <CheckCircle size={18} />
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Cancelled Bookings */}
            <Col md="3" xl="2" className="col-6">
              <Card 
                className={`card-animate cursor-pointer border-bottom border-3 border-danger ${activeFilter === "CANCELLED" ? "bg-soft-danger" : ""}`}
                onClick={() => openListModal("bookings", "CANCELLED", "Cancelled Bookings")}
              >
                <CardBody className="p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted text-truncate mb-1">Cancelled</p>
                      <h4 className="mb-0 fw-bold text-danger">{stats.summary.CancelledBookings}</h4>
                    </div>
                    <div className="avatar-xs">
                      <span className="avatar-title rounded bg-soft-danger text-danger">
                        <XCircle size={18} />
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Online Drivers */}
            <Col md="3" xl="2" className="col-6">
              <Card 
                className="card-animate cursor-pointer border-bottom border-3 border-dark"
                onClick={() => openListModal("drivers", "ONLINE", "Online Drivers")}
              >
                <CardBody className="p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted text-truncate mb-1">Drivers Online</p>
                      <h4 className="mb-0 fw-bold text-dark">
                        {stats.summary.OnlineDrivers} <span className="fs-13 fw-normal text-muted">/ {stats.summary.TotalDrivers}</span>
                      </h4>
                    </div>
                    <div className="avatar-xs">
                      <span className="avatar-title rounded bg-soft-dark text-dark">
                        <Users size={18} />
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Map and Charts Row */}
          <Row>
            {/* Leaflet Live Fleet Map */}
            <Col lg="8">
              <Card className="border-0 shadow-sm" style={{ minHeight: "500px" }}>
                <CardBody className="p-0" style={{ position: "relative" }}>
                  <div className="p-3 bg-light border-bottom d-flex align-items-center justify-content-between">
                    <h5 className="card-title mb-0">Live Driver Tracking & Fleet Coverage</h5>
                    <Badge color="success" pill className="p-2 animate-pulse">
                      ● Live Socket Sync Active
                    </Badge>
                  </div>
                  
                  {/* Map Container */}
                  <div style={{ height: "450px", width: "100%", overflow: "hidden" }}>
                    <MapContainer 
                      center={mapCenter} 
                      zoom={13} 
                      style={{ height: "100%", width: "100%" }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      
                      <ChangeMapCenter center={mapCenter} />

                      {stats.drivers && stats.drivers
                        .filter(d => d.Latitude && d.Longitude)
                        .map(driver => {
                          const lat = parseFloat(driver.Latitude);
                          const lng = parseFloat(driver.Longitude);
                          if (isNaN(lat) || isNaN(lng)) return null;

                          return (
                            <Marker
                              key={driver.DriverId}
                              position={[lat, lng]}
                              icon={getDriverIcon(driver.IsOnline, driver.IsBusy)}
                            >
                              <Popup>
                                <div className="p-1">
                                  <h6 className="fw-bold mb-1">{driver.DriverName}</h6>
                                  <p className="text-muted mb-1 fs-12">
                                    Mobile: <b>{driver.MobileNo || "N/A"}</b>
                                  </p>
                                  <p className="mb-1 fs-12">
                                    Vehicle Plate: <b>{driver.VehicleNo || "N/A"}</b>
                                  </p>
                                  <div className="d-flex gap-1 mt-2 mb-2">
                                    <Badge color={driver.IsOnline ? "success" : "secondary"}>
                                      {driver.IsOnline ? "Online" : "Offline"}
                                    </Badge>
                                    <Badge color={driver.IsBusy ? "warning" : "info"}>
                                      {driver.IsBusy ? `In-Trip (${driver.ActiveBookingId})` : "Available"}
                                    </Badge>
                                  </div>
                                  <Button 
                                    color="primary" 
                                    size="xs" 
                                    className="w-100 fs-11"
                                    onClick={() => openDriverDetails(driver.DriverId)}
                                  >
                                    View Full Dossier
                                  </Button>
                                </div>
                              </Popup>
                            </Marker>
                          );
                        })}
                    </MapContainer>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Sidebar Donut & Registry List */}
            <Col lg="4">
              <Card className="border-0 shadow-sm mb-4" style={{ height: "235px" }}>
                <CardBody className="p-3">
                  <h5 className="card-title mb-3">Driver Allocation (Click segment to list)</h5>
                  <ReactApexChart 
                    options={donutOptions} 
                    series={donutSeries} 
                    type="donut" 
                    height={160} 
                  />
                </CardBody>
              </Card>

              {/* Online Driver Registry list */}
              <Card className="border-0 shadow-sm" style={{ height: "235px" }}>
                <CardBody className="p-3 d-flex flex-column">
                  <h5 className="card-title mb-3">Active Fleet Registry</h5>
                  <div className="flex-grow-1 overflow-auto" style={{ maxHeight: "160px" }}>
                    <Table hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Driver (Click to center)</th>
                          <th>Vehicle</th>
                          <th>Profile</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.drivers && stats.drivers.map(driver => (
                          <tr 
                            key={driver.DriverId} 
                            className="align-middle"
                          >
                            <td 
                              onClick={() => handleMarkerClick(driver.Latitude, driver.Longitude, driver.DriverId)}
                              className="cursor-pointer fw-semibold text-primary"
                            >
                              <span className="d-block text-truncate" style={{ maxWidth: "120px" }}>
                                {driver.DriverName}
                              </span>
                            </td>
                            <td>{driver.VehicleNo || "N/A"}</td>
                            <td>
                              <Button 
                                size="xs" 
                                color="light" 
                                className="px-2"
                                onClick={() => openDriverDetails(driver.DriverId)}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Chart Distribution Row */}
          <Row className="mt-4">
            <Col lg="12">
              <Card className="border-0 shadow-sm">
                <CardBody>
                  <h5 className="card-title mb-4">Detailed Booking Status Frequency (Click bars to list)</h5>
                  <ReactApexChart 
                    options={barChartOptions} 
                    series={barChartSeries} 
                    type="bar" 
                    height={300} 
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* General Bookings Table */}
          <Row className="mt-4">
            <Col lg="12">
              <Card className="border-0 shadow-sm">
                <CardBody>
                  <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-4 gap-3">
                    <h5 className="card-title mb-0">
                      Bookings Registry Dashboard
                    </h5>
                    
                    {/* Search and Filters */}
                    <div className="d-flex gap-3 align-items-center">
                      <div className="d-flex btn-group bg-light rounded p-1">
                        <Button size="sm" color={activeFilter === "ALL" ? "primary" : "light"} onClick={() => setActiveFilter("ALL")}>All</Button>
                        <Button size="sm" color={activeFilter === "PENDING" ? "primary" : "light"} onClick={() => setActiveFilter("PENDING")}>Pending</Button>
                        <Button size="sm" color={activeFilter === "ONGOING" ? "primary" : "light"} onClick={() => setActiveFilter("ONGOING")}>Ongoing</Button>
                        <Button size="sm" color={activeFilter === "COMPLETED" ? "primary" : "light"} onClick={() => setActiveFilter("COMPLETED")}>Completed</Button>
                        <Button size="sm" color={activeFilter === "CANCELLED" ? "primary" : "light"} onClick={() => setActiveFilter("CANCELLED")}>Cancelled</Button>
                      </div>

                      <InputGroup style={{ maxWidth: "250px" }}>
                        <Input 
                          placeholder="Search bookings..." 
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <span className="input-group-text bg-light">
                          <Search size={16} />
                        </span>
                      </InputGroup>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <Table hover striped bordered className="mb-0 align-middle">
                      <thead className="bg-light text-center">
                        <tr>
                          <th>Booking ID</th>
                          <th>Date</th>
                          <th>Customer</th>
                          <th>Driver / Vehicle</th>
                          <th>Pickup Address</th>
                          <th>Price</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBookings.length > 0 ? (
                          filteredBookings.map(b => (
                            <tr key={b.Id} className="text-center">
                              <td>
                                <span className="fw-bold text-dark">#{b.Id}</span>
                              </td>
                              <td className="fs-13 text-muted">
                                {b.BookingDate ? new Date(b.BookingDate).toLocaleDateString() : "N/A"}
                              </td>
                              <td className="text-start">
                                <span className="fw-semibold d-block text-dark">{b.CustomerName || "N/A"}</span>
                                <span className="text-muted fs-12">{b.CustomerMobile || ""}</span>
                              </td>
                              <td className="text-start">
                                {b.DriverName ? (
                                  <>
                                    <span className="fw-semibold d-block text-dark">{b.DriverName}</span>
                                    <span className="text-muted fs-12">{b.VehicleNo} ({b.VehicleName || "Vehicle"})</span>
                                  </>
                                ) : (
                                  <span className="text-muted fst-italic">Not Assigned</span>
                                )}
                              </td>
                              <td className="text-start fs-13" title={b.PickupAddress} style={{ maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {b.PickupAddress || "N/A"}
                              </td>
                              <td>
                                <span className="fw-bold text-success">₹{(b.Price || 0).toLocaleString()}</span>
                              </td>
                              <td>
                                <Badge 
                                  color={
                                    b.StatusId === 1 ? "info" : 
                                    b.StatusId >= 2 && b.StatusId <= 16 ? "warning" : 
                                    b.StatusId === 17 || b.StatusId === 18 ? "success" : 
                                    "danger"
                                  }
                                  pill
                                >
                                  {b.StatusName || "Unknown"}
                                </Badge>
                              </td>
                              <td>
                                <Button 
                                  color="primary" 
                                  size="sm"
                                  onClick={() => openBookingDetails(b)}
                                >
                                  View Details
                                </Button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center py-4 text-muted">
                              No bookings found matching search criteria.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* ========================================================================= */}
          {/* MODAL 1: DRILL-DOWN BOOKINGS / DRIVERS GENERAL LIST */}
          {/* ========================================================================= */}
          <Modal 
            isOpen={listModalOpen} 
            toggle={() => setListModalOpen(!listModalOpen)} 
            size="lg" 
            centered
          >
            <ModalHeader toggle={() => setListModalOpen(!listModalOpen)} className="bg-light">
              <Truck size={18} className="me-2 text-primary" />
              Listing Records: {listModalFilter}
            </ModalHeader>
            <ModalBody style={{ maxHeight: "450px", overflowY: "auto" }}>
              {listModalType === "bookings" ? (
                <Table striped hover size="sm" className="mb-0 align-middle">
                  <thead>
                    <tr className="text-center">
                      <th>ID</th>
                      <th>Customer</th>
                      <th>Driver</th>
                      <th>Pickup Address</th>
                      <th>Price</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalListData.map(b => (
                      <tr key={b.Id} className="text-center">
                        <td><b>#{b.Id}</b></td>
                        <td className="text-start">{b.CustomerName || "N/A"}</td>
                        <td className="text-start">{b.DriverName || "Unassigned"}</td>
                        <td className="text-start text-truncate" style={{ maxWidth: "200px" }}>{b.PickupAddress}</td>
                        <td><span className="fw-bold text-success">₹{(b.Price || 0).toLocaleString()}</span></td>
                        <td>
                          <Button 
                            color="primary" 
                            size="xs"
                            onClick={() => {
                              openBookingDetails(b);
                            }}
                          >
                            Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Table striped hover size="sm" className="mb-0 align-middle">
                  <thead>
                    <tr className="text-center">
                      <th>Driver Name</th>
                      <th>Mobile</th>
                      <th>Vehicle Plate</th>
                      <th>Online</th>
                      <th>Job Status</th>
                      <th>Dossier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalListData.map(d => (
                      <tr key={d.DriverId} className="text-center">
                        <td className="text-start fw-semibold">{d.DriverName}</td>
                        <td>{d.MobileNo || "N/A"}</td>
                        <td>{d.VehicleNo || "N/A"}</td>
                        <td>
                          <Badge color={d.IsOnline ? "success" : "secondary"}>
                            {d.IsOnline ? "Online" : "Offline"}
                          </Badge>
                        </td>
                        <td>
                          <Badge color={d.IsBusy ? "warning" : "info"}>
                            {d.IsBusy ? `Busy (Booking #${d.ActiveBookingId})` : "Free"}
                          </Badge>
                        </td>
                        <td>
                          <Button 
                            color="primary" 
                            size="xs"
                            onClick={() => {
                              openDriverDetails(d.DriverId);
                            }}
                          >
                            Open
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </ModalBody>
            <ModalFooter className="bg-light">
              <Button color="secondary" onClick={() => setListModalOpen(false)}>Close</Button>
            </ModalFooter>
          </Modal>

          {/* ========================================================================= */}
          {/* MODAL 2: DEEP DRILL-DOWN INDIVIDUAL BOOKING DETAILS */}
          {/* ========================================================================= */}
          <Modal 
            isOpen={bookingModalOpen} 
            toggle={() => setBookingModalOpen(!bookingModalOpen)} 
            size="xl" 
            centered
          >
            <ModalHeader toggle={() => setBookingModalOpen(!bookingModalOpen)} className="bg-dark text-white">
              <Truck className="me-2 text-warning" size={18} />
              Operations Dossier: Booking #{selectedBooking?.Id}
            </ModalHeader>
            <ModalBody className="bg-light">
              {selectedBooking && (
                <Container fluid className="px-0">
                  <Row>
                    {/* Left Panel: Basic info and Stops */}
                    <Col md="7">
                      {/* Basic details */}
                      <Card className="border-0 shadow-sm mb-3">
                        <CardBody className="p-3">
                          <h6 className="fw-bold border-bottom pb-2 mb-3 text-primary d-flex align-items-center">
                            <Activity size={16} className="me-2" /> Booking Lifecycle Overview
                          </h6>
                          <Row>
                            <Col sm="6">
                              <p className="mb-1 text-muted fs-12">Booking Reference No</p>
                              <p className="fw-bold mb-3">{selectedBooking.BookingNo || "Not Allocated"}</p>
                              
                              <p className="mb-1 text-muted fs-12">Fare Details</p>
                              <p className="fw-bold mb-3 text-success fs-16">₹{(selectedBooking.Price || 0).toLocaleString()}</p>
                            </Col>
                            <Col sm="6">
                              <p className="mb-1 text-muted fs-12">Scheduled Booking Date</p>
                              <p className="fw-bold mb-3"><Calendar size={13} className="me-1" />{selectedBooking.BookingDate ? new Date(selectedBooking.BookingDate).toLocaleString() : "N/A"}</p>

                              <p className="mb-1 text-muted fs-12">Cargo details / Goods Type</p>
                              <p className="fw-bold mb-0">
                                {selectedBooking.GoodsType || "General"}
                                {selectedBooking.ItemWeight && ` (Weight: ${selectedBooking.ItemWeight})`}
                              </p>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>

                      {/* Stops timeline */}
                      <Card className="border-0 shadow-sm">
                        <CardBody className="p-3">
                          <h6 className="fw-bold border-bottom pb-2 mb-3 text-primary d-flex align-items-center">
                            <MapPin size={16} className="me-2" /> Route Timeline ({selectedBooking.StopCount || 1} Stops)
                          </h6>
                          
                          <div className="ps-2" style={{ borderLeft: "2px dashed #dee2e6", marginLeft: "10px" }}>
                            {/* Pickup */}
                            <div className="mb-3 position-relative">
                              <div style={{ position: "absolute", left: "-16px", top: "2px", width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#3498db" }}></div>
                              <span className="text-muted fs-11 d-block uppercase fw-bold">PICKUP ADDRESS</span>
                              <span className="fw-semibold text-dark fs-13">{selectedBooking.PickupAddress}</span>
                            </div>

                            {/* Stops */}
                            {selectedBooking.Stop1Address && (
                              <div className="mb-3 position-relative">
                                <div style={{ position: "absolute", left: "-16px", top: "2px", width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#e67e22" }}></div>
                                <span className="text-muted fs-11 d-block uppercase fw-bold">STOP 1</span>
                                <span className="fw-semibold text-dark fs-13">{selectedBooking.Stop1Address}</span>
                                {selectedBooking.Stop1Photo && (
                                  <div className="mt-2">
                                    <Badge color="light" className="text-primary border p-1" style={{ cursor: "pointer" }} onClick={() => window.open(`${API_WEB_URLS.IMAGEURL}${selectedBooking.Stop1Photo}`)}>
                                      👁️ View Stop 1 Verification Photo
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            )}

                            {selectedBooking.Stop2Address && (
                              <div className="mb-3 position-relative">
                                <div style={{ position: "absolute", left: "-16px", top: "2px", width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#e67e22" }}></div>
                                <span className="text-muted fs-11 d-block uppercase fw-bold">STOP 2</span>
                                <span className="fw-semibold text-dark fs-13">{selectedBooking.Stop2Address}</span>
                                {selectedBooking.Stop2Photo && (
                                  <div className="mt-2">
                                    <Badge color="light" className="text-primary border p-1" style={{ cursor: "pointer" }} onClick={() => window.open(`${API_WEB_URLS.IMAGEURL}${selectedBooking.Stop2Photo}`)}>
                                      👁️ View Stop 2 Verification Photo
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            )}

                            {selectedBooking.Stop3Address && (
                              <div className="mb-3 position-relative">
                                <div style={{ position: "absolute", left: "-16px", top: "2px", width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#e67e22" }}></div>
                                <span className="text-muted fs-11 d-block uppercase fw-bold">STOP 3</span>
                                <span className="fw-semibold text-dark fs-13">{selectedBooking.Stop3Address}</span>
                                {selectedBooking.Stop3Photo && (
                                  <div className="mt-2">
                                    <Badge color="light" className="text-primary border p-1" style={{ cursor: "pointer" }} onClick={() => window.open(`${API_WEB_URLS.IMAGEURL}${selectedBooking.Stop3Photo}`)}>
                                      👁️ View Stop 3 Verification Photo
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            )}

                            {selectedBooking.Stop4Address && (
                              <div className="mb-0 position-relative">
                                <div style={{ position: "absolute", left: "-16px", top: "2px", width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#27ae60" }}></div>
                                <span className="text-muted fs-11 d-block uppercase fw-bold">STOP 4 (FINAL DROP)</span>
                                <span className="fw-semibold text-dark fs-13">{selectedBooking.Stop4Address}</span>
                                {selectedBooking.Stop4Photo && (
                                  <div className="mt-2">
                                    <Badge color="light" className="text-primary border p-1" style={{ cursor: "pointer" }} onClick={() => window.open(`${API_WEB_URLS.IMAGEURL}${selectedBooking.Stop4Photo}`)}>
                                      👁️ View Stop 4 Verification Photo
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </CardBody>
                      </Card>
                    </Col>

                    {/* Right Panel: Parties, Security, and Fares */}
                    <Col md="5">
                      {/* Customer & Driver Profile */}
                      <Card className="border-0 shadow-sm mb-3">
                        <CardBody className="p-3">
                          <h6 className="fw-bold border-bottom pb-2 mb-3 text-primary d-flex align-items-center">
                            <Users size={16} className="me-2" /> Dispatch Profiles
                          </h6>
                          <div className="mb-3">
                            <span className="text-muted fs-12 d-block">CUSTOMER</span>
                            <span className="fw-semibold text-dark fs-14">{selectedBooking.CustomerName || "N/A"}</span>
                            <span className="text-muted d-block fs-12">Contact: {selectedBooking.CustomerMobile || "N/A"}</span>
                          </div>
                          <div>
                            <span className="text-muted fs-12 d-block">ASSIGNED DRIVER & VEHICLE</span>
                            {selectedBooking.DriverName ? (
                              <div className="cursor-pointer text-primary" onClick={() => {
                                setBookingModalOpen(false);
                                openDriverDetails(selectedBooking.F_VehicleMaster);
                              }}>
                                <span className="fw-semibold fs-14 d-block">{selectedBooking.DriverName} (Click to open file)</span>
                                <span className="text-muted d-block fs-12">Plate Number: <b>{selectedBooking.VehicleNo || "N/A"}</b></span>
                                <span className="text-muted d-block fs-12">Model: {selectedBooking.VehicleName || "N/A"}</span>
                              </div>
                            ) : (
                              <span className="text-danger fw-semibold fs-13">Waiting for nearby driver matching...</span>
                            )}
                          </div>
                        </CardBody>
                      </Card>

                      {/* Security Verification & OTP */}
                      <Card className="border-0 shadow-sm mb-3">
                        <CardBody className="p-3">
                          <h6 className="fw-bold border-bottom pb-2 mb-3 text-primary d-flex align-items-center">
                            <Shield size={16} className="me-2" /> Dispatch Security Keys
                          </h6>
                          <Row>
                            <Col xs="6">
                              <span className="text-muted fs-12 d-block">START TRIP OTP</span>
                              <Badge color="light" className="text-dark border fs-14 px-2 py-1 mt-1 font-monospace">
                                {selectedBooking.OTP || "N/A"}
                              </Badge>
                            </Col>
                            <Col xs="6">
                              <span className="text-muted fs-12 d-block">DELIVERY COMPLETED OTP</span>
                              <Badge color="light" className="text-dark border fs-14 px-2 py-1 mt-1 font-monospace">
                                {selectedBooking.DeliveryOTP || "N/A"}
                              </Badge>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>

                      {/* Financial breakdown */}
                      <Card className="border-0 shadow-sm">
                        <CardBody className="p-3">
                          <h6 className="fw-bold border-bottom pb-2 mb-3 text-primary d-flex align-items-center">
                            <DollarSign size={16} className="me-2" /> Fare Breakdown
                          </h6>
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted fs-13">Trip Distance ({selectedBooking.TotalTripKM || 0} KM)</span>
                            <span className="fw-semibold">₹{(selectedBooking.BaseFare || 0).toLocaleString()}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted fs-13">Customer Charges / Stops Fee</span>
                            <span className="fw-semibold">₹{(selectedBooking.ChargesCustomer || 0).toLocaleString()}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted fs-13">Driver Charges / Waiting Fee</span>
                            <span className="fw-semibold">₹{(selectedBooking.ChargesDriver || 0).toLocaleString()}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-2 text-danger">
                            <span className="fs-13">Cancellation Penalty</span>
                            <span>+ ₹{(selectedBooking.CancellationCharge || 0).toLocaleString()}</span>
                          </div>
                          <hr className="my-2" />
                          <div className="d-flex justify-content-between mb-0 fw-bold fs-15 text-success">
                            <span>Final Amount Charged</span>
                            <span>₹{(selectedBooking.Price || 0).toLocaleString()}</span>
                          </div>
                          
                          {/* Rating details */}
                          {selectedBooking.RatingsCustomer && (
                            <div className="mt-3 p-2 bg-light rounded border">
                              <span className="text-muted fs-11 d-block uppercase font-weight-bold">CUSTOMER REVIEW</span>
                              <span className="text-warning">{"★".repeat(selectedBooking.RatingsCustomer)}</span>
                              <span className="fs-12 d-block text-italic">"{selectedBooking.RatingsCustomerDes || "No comment provided."}"</span>
                            </div>
                          )}
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </Container>
              )}
            </ModalBody>
            <ModalFooter className="bg-light">
              <Button color="secondary" onClick={() => setBookingModalOpen(false)}>Close Dossier</Button>
            </ModalFooter>
          </Modal>

          {/* ========================================================================= */}
          {/* MODAL 3: DEEP DRILL-DOWN DRIVER DOSSIER & PROFILE */}
          {/* ========================================================================= */}
          <Modal 
            isOpen={driverModalOpen} 
            toggle={() => setDriverModalOpen(!driverModalOpen)} 
            size="lg" 
            centered
          >
            <ModalHeader toggle={() => setDriverModalOpen(!driverModalOpen)} className="bg-dark text-white">
              <Users className="me-2 text-success" size={18} />
              Driver Credentials & Profile File: {selectedDriver?.DriverName}
            </ModalHeader>
            <ModalBody className="bg-light">
              {selectedDriver && (
                <Container fluid className="px-0">
                  <Row>
                    {/* Left: General & Vehicle details */}
                    <Col md="6">
                      {/* General */}
                      <Card className="border-0 shadow-sm mb-3">
                        <CardBody className="p-3">
                          <h6 className="fw-bold border-bottom pb-2 mb-3 text-primary d-flex align-items-center">
                            <Users size={16} className="me-2" /> Personal Registry
                          </h6>
                          <div className="mb-2">
                            <span className="text-muted fs-12 d-block">Driver Mobile</span>
                            <span className="fw-semibold text-dark fs-13">{selectedDriver.MobileNo || "N/A"}</span>
                          </div>
                          <div className="mb-2">
                            <span className="text-muted fs-12 d-block">Email Address</span>
                            <span className="fw-semibold text-dark fs-13">{selectedDriver.Email || "N/A"}</span>
                          </div>
                          <div className="mb-2">
                            <span className="text-muted fs-12 d-block">Physical Address</span>
                            <span className="fw-semibold text-dark fs-13">{selectedDriver.Address || "N/A"}</span>
                          </div>
                          <Row>
                            <Col xs="6">
                              <span className="text-muted fs-12 d-block">Age / DOB</span>
                              <span className="fw-semibold text-dark fs-13">{selectedDriver.Age || "N/A"} Yrs</span>
                            </Col>
                            <Col xs="6">
                              <span className="text-muted fs-12 d-block">Join Date</span>
                              <span className="fw-semibold text-dark fs-13">{selectedDriver.DateOfJoining ? new Date(selectedDriver.DateOfJoining).toLocaleDateString() : "N/A"}</span>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>

                      {/* Vehicle detail */}
                      <Card className="border-0 shadow-sm">
                        <CardBody className="p-3">
                          <h6 className="fw-bold border-bottom pb-2 mb-3 text-primary d-flex align-items-center">
                            <Truck size={16} className="me-2" /> Fleet Assignment Details
                          </h6>
                          <div className="mb-2">
                            <span className="text-muted fs-12 d-block">Vehicle Registration Name</span>
                            <span className="fw-semibold text-dark fs-13">{selectedDriver.VehicleName || "N/A"}</span>
                          </div>
                          <div className="mb-2">
                            <span className="text-muted fs-12 d-block">Vehicle Plate Number</span>
                            <span className="fw-bold text-dark fs-13">{selectedDriver.VehicleNo || "N/A"}</span>
                          </div>
                          <Row>
                            <Col xs="6">
                              <span className="text-muted fs-12 d-block">Weight Capacity</span>
                              <span className="fw-semibold text-dark fs-13">{selectedDriver.WeightCapacity || "N/A"} KG</span>
                            </Col>
                            <Col xs="6">
                              <span className="text-muted fs-12 d-block">Vehicle Body Type</span>
                              <span className="fw-semibold text-dark fs-13">{selectedDriver.VehicleBodyType || "N/A"}</span>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                    </Col>

                    {/* Right: Bank Details and Verification Documents */}
                    <Col md="6">
                      {/* Bank profile */}
                      <Card className="border-0 shadow-sm mb-3">
                        <CardBody className="p-3">
                          <h6 className="fw-bold border-bottom pb-2 mb-3 text-primary d-flex align-items-center">
                            <CreditCard size={16} className="me-2" /> Banking & Settlement Ledger
                          </h6>
                          <div className="mb-2">
                            <span className="text-muted fs-12 d-block">Settlement Account Person Name</span>
                            <span className="fw-semibold text-dark fs-13">{selectedDriver.AccountPersonName || "N/A"}</span>
                          </div>
                          <div className="mb-2">
                            <span className="text-muted fs-12 d-block">Bank Account Number</span>
                            <span className="fw-bold text-dark fs-13 font-monospace">{selectedDriver.AccountNo || "N/A"}</span>
                          </div>
                          <Row>
                            <Col xs="6">
                              <span className="text-muted fs-12 d-block">Bank Name</span>
                              <span className="fw-semibold text-dark fs-13">{selectedDriver.BankName || "N/A"}</span>
                            </Col>
                            <Col xs="6">
                              <span className="text-muted fs-12 d-block">IFSC Code</span>
                              <span className="fw-bold text-dark fs-13 font-monospace">{selectedDriver.IFSC || "N/A"}</span>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>

                      {/* Documents verification */}
                      <Card className="border-0 shadow-sm">
                        <CardBody className="p-3">
                          <h6 className="fw-bold border-bottom pb-2 mb-3 text-primary d-flex align-items-center">
                            <Shield size={16} className="me-2" /> Verification Dossier Images
                          </h6>
                          <div className="d-flex flex-column gap-2" style={{ maxHeight: "150px", overflowY: "auto" }}>
                            {selectedDriver.AadharFront && (
                              <div className="d-flex justify-content-between align-items-center border-bottom pb-1">
                                <span className="fs-12">Aadhar Front Image</span>
                                <Button size="xs" color="link" onClick={() => window.open(`${API_WEB_URLS.IMAGEURL}${selectedDriver.AadharFront}`)}>Open Document</Button>
                              </div>
                            )}
                            {selectedDriver.AadharBack && (
                              <div className="d-flex justify-content-between align-items-center border-bottom pb-1">
                                <span className="fs-12">Aadhar Back Image</span>
                                <Button size="xs" color="link" onClick={() => window.open(`${API_WEB_URLS.IMAGEURL}${selectedDriver.AadharBack}`)}>Open Document</Button>
                              </div>
                            )}
                            {selectedDriver.RCFront && (
                              <div className="d-flex justify-content-between align-items-center border-bottom pb-1">
                                <span className="fs-12">Registration Certificate (RC) Front</span>
                                <Button size="xs" color="link" onClick={() => window.open(`${API_WEB_URLS.IMAGEURL}${selectedDriver.RCFront}`)}>Open Document</Button>
                              </div>
                            )}
                            {selectedDriver.DrivingLicenseFront && (
                              <div className="d-flex justify-content-between align-items-center border-bottom pb-1">
                                <span className="fs-12">Driving License Front</span>
                                <Button size="xs" color="link" onClick={() => window.open(`${API_WEB_URLS.IMAGEURL}${selectedDriver.DrivingLicenseFront}`)}>Open Document</Button>
                              </div>
                            )}
                            {selectedDriver.Insurance && (
                              <div className="d-flex justify-content-between align-items-center pb-1">
                                <span className="fs-12">Vehicle Insurance Copy</span>
                                <Button size="xs" color="link" onClick={() => window.open(`${API_WEB_URLS.IMAGEURL}${selectedDriver.Insurance}`)}>Open Document</Button>
                              </div>
                            )}
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </Container>
              )}
            </ModalBody>
            <ModalFooter className="bg-light">
              <Button color="secondary" onClick={() => setDriverModalOpen(false)}>Close Dossier</Button>
            </ModalFooter>
          </Modal>

        </Container>
      </div>
    </React.Fragment>
  );
};

Dashboard.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(Dashboard);
